import { createAction } from '@reduxjs/toolkit'
import { call, fork, ForkEffect, put, select, takeLatest } from 'redux-saga/effects'

import { pollProjectForRegionUpdate } from '../effects/pollProjectForRegionUpdate'
import { regenerateMeasurementsByProjectAndDocumentIds } from './saveUpdatedCalibrationFactor'
import { fetchOpeningGroupsByProjectIdWithDefault, updateRegion } from '../../../api/takeoff-api'
import { OpeningGroupAPI } from '../../../models/activeDrawable'
import { Project } from '../../../models/project'
import { Region } from '../../../models/region'
import { IUpdatedRegionResponse } from '../../../models/updatedRegionResponse'
import { convertScaleEnumToString } from '../../../utils/calculations/scaleConversion/scaleConversion'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { RegionTool, Select, Workspace } from '../../lib/toolBoxes/2D'
import { selectProject } from '../../slices/project'
import { resetRegionFormState, selectAllRegions, updateSingleRegion } from '../../slices/region'
import { IMUP2DCoordinatesToUpdate } from '../../types'

export const updateRegionFromId = createAction<{
    regionId: number
    coordinatesToUpdate?: IMUP2DCoordinatesToUpdate
    propertiesToUpdate?: Partial<Region>
}>('updateRegionFromId')

export function* handleUpdateRegionFromId({ payload }: ReturnType<typeof updateRegionFromId>) {
    const paperManager: PaperManager | null = yield call(managers.get2DManager)

    if (!paperManager) return

    try {
        const { regionId, coordinatesToUpdate, propertiesToUpdate } = payload

        const project: Project = yield select(selectProject)
        const openingGroups: OpeningGroupAPI[] = yield call(fetchOpeningGroupsByProjectIdWithDefault, project.id)

        const [selectTool, workspaceTool, regionTool]: [Select, Workspace, RegionTool] = yield call(
            paperManager.getTools,
            [Select.NAME, Workspace.NAME, RegionTool.NAME]
        )

        const allRegions: Region[] = yield select(selectAllRegions)
        const currentRegion = allRegions.find((r) => r.id === regionId)

        if (!currentRegion) return

        const documentChunkId = propertiesToUpdate?.document_chunk_id ?? currentRegion.document_chunk_id

        const updatedRegion: Omit<Region, 'id'> = {
            document_chunk_id: documentChunkId,
            name: propertiesToUpdate?.name ?? currentRegion.name,
            type: propertiesToUpdate?.type ?? currentRegion.type,
            scale: propertiesToUpdate?.scale ?? currentRegion.scale,
            coordinates: coordinatesToUpdate?.coordinates ?? currentRegion.coordinates,
        }

        const regionScaleChange = currentRegion.scale !== updatedRegion.scale
        const response: IUpdatedRegionResponse = yield call(updateRegion, project.id, currentRegion.id, updatedRegion)

        if (response.updatedRegion) {
            yield put(updateSingleRegion(response.updatedRegion))
        }

        const regionItem: paper.Path | null = yield call(workspaceTool.getItemWithRegionId, regionId)

        // If updating the scale on a region, update the associated
        // regions label text to reflect the new scale
        if (propertiesToUpdate && propertiesToUpdate.scale && regionItem) {
            yield call(
                workspaceTool.updateScaleForRegion,
                regionItem.id,
                convertScaleEnumToString(propertiesToUpdate.scale)
            )

            yield call(regenerateMeasurementsByProjectAndDocumentIds, paperManager, project.id, documentChunkId)
        }

        // If the coordinates are updated then
        // update the position of the label text
        if (coordinatesToUpdate && coordinatesToUpdate.coordinates && regionItem) {
            const labelItem: paper.TextItem | null = yield call(
                workspaceTool.getItemWithPaperId,
                regionItem.data.labelId
            )

            if (labelItem) {
                yield call(regionTool.updateScaleTextPositionRelativeToRegionPath, labelItem, regionItem)
            }
        }

        if (!coordinatesToUpdate) {
            yield call(selectTool.exitSelectMode)
            yield put(resetRegionFormState())
        }

        // Use updated coordinates if provided, otherwise use current region coordinates
        const coordinates = coordinatesToUpdate?.coordinates ?? currentRegion.coordinates

        yield fork(
            pollProjectForRegionUpdate,
            'Recalculating measurements…',
            5000,
            openingGroups,
            regionScaleChange,
            regionId,
            coordinates
        )
    } catch (error) {
        yield call(console.error, error)
    }
}

export function* watchForUpdateRegionFromId(): Generator<ForkEffect<never>, void, unknown> {
    yield takeLatest(updateRegionFromId.type, handleUpdateRegionFromId)
}
